import * as THREE from "three";
import type { Cup } from "./types";
import { createCupObject, getShapeDef } from "./cup";

/**
 * Distribute cups across the shelf rows defined in scene.ts. The shelf
 * exposes its row Y coordinates and width via userData; we compute even
 * spacing per row and place each cup group accordingly. Returns a map of
 * cup id -> { group, cup } for picking and a parallel list for animation.
 */
export function placeCups(
  cupRoot: THREE.Group,
  shelf: THREE.Group,
  cups: Cup[],
): Map<string, { group: THREE.Group; cup: Cup }> {
  const rowYs = shelf.userData.rowYs as number[];
  const rowWidth = shelf.userData.rowWidth as number;
  const map = new Map<string, { group: THREE.Group; cup: Cup }>();

  const perRow = Math.ceil(cups.length / rowYs.length);
  const padding = 1.0;
  const usable = rowWidth - padding * 2;
  const step = perRow > 1 ? usable / (perRow - 1) : 0;

  cups.forEach((cup, i) => {
    const rowIndex = Math.floor(i / perRow);
    const colIndex = i % perRow;
    const x = -usable / 2 + colIndex * step;
    // shelf top + half thickness — and subtract the shape's baseY so the
    // bottom-most point of the lathe profile sits exactly on the shelf
    // surface. Standard shape has baseY=0 so this is a no-op for legacy
    // cups; shorter / sunken-base shapes (espresso) get the right contact.
    const shape = getShapeDef(cup.shape);
    const y = (rowYs[rowIndex] ?? rowYs[0]) + 0.06 - shape.baseY;
    const z = 0;

    const obj = createCupObject(cup);
    obj.group.position.set(x, y, z);
    // Default cup orientation places the handle on +X. The camera sits in
    // the +X+Z quadrant, so rotating +π/4 around Y points the handle into
    // +X−Z — 45° away from camera. Result: a 3/4 product-photo angle
    // where the handle is visible on the right but doesn't occlude the
    // cup's wrap design.
    obj.group.rotation.y = Math.PI / 4;

    cupRoot.add(obj.group);
    map.set(cup.id, { group: obj.group, cup });
  });

  return map;
}
